import type { Box } from "./closed-frame-extent";
import { footprintBox } from "./footprint-box";
import type { HitSurface } from "./hit-area";

/**
 * Which DOM elements ARE the launcher, the peek and the auth popup.
 *
 * The iframe embed sizes its transparent frame from their boxes. Looking them up with
 * `document.querySelector("[data-wx-fab]")` is unsafe: the panel (which holds operator / RSS
 * article HTML) precedes the launcher in the DOM, so content carrying that attribute plus a
 * `style` won and could stretch the frame over the whole host page. The launcher components
 * register their own element here, so the lookup is by IDENTITY, not by attribute.
 *
 * One widget per document is assumed (the iframe embed, the only consumer); a later
 * registration of the same kind replaces the earlier one.
 */
export type AnchorKind = "fab" | "peek" | "popup";

export const CLOSED_ANCHOR_KINDS: readonly AnchorKind[] = ["fab", "peek", "popup"];
const anchors = new Map<AnchorKind, HTMLElement>();

/** Register `el` as the anchor of `kind`; returns the unregister function. */
export function registerAnchor(kind: AnchorKind, el: HTMLElement): () => void {
  anchors.set(kind, el);
  return () => {
    if (anchors.get(kind) === el) anchors.delete(kind);
  };
}

/** Stable callback refs for the launcher components: `ref={ANCHOR_REFS.fab}`. React 19 runs the
 *  returned function on unmount. Module-level so a re-render never detaches / re-attaches. */
export const ANCHOR_REFS: Record<AnchorKind, (el: HTMLElement | null) => (() => void) | undefined> =
  {
    fab: (el) => (el ? registerAnchor("fab", el) : undefined),
    peek: (el) => (el ? registerAnchor("peek", el) : undefined),
    popup: (el) => (el ? registerAnchor("popup", el) : undefined),
  };

export function getAnchor(kind: AnchorKind): HTMLElement | null {
  return anchors.get(kind) ?? null;
}

/** Boxes of the registered closed-state elements (launcher, peek, popup). */
export function closedFootprintBoxes(): Box[] {
  const boxes: Box[] = [];
  for (const kind of CLOSED_ANCHOR_KINDS) {
    const el = anchors.get(kind);
    if (el) boxes.push(footprintBox(el));
  }
  return boxes;
}

const viewportBox = (el: Element): Box => {
  const r = el.getBoundingClientRect();
  return { left: r.left, top: r.top, width: r.width, height: r.height };
};

/**
 * The visible surfaces of the closed widget, for clipping the iframe's hit area: the launcher and
 * the auth popup whole, and inside the peek only the parts marked `data-wx-hit` (the message
 * bubble and each quick reply), each cut to the peek box (the reply list scrolls inside it).
 * Looked up inside the registered anchors only, never document-wide.
 */
export function closedHitSurfaces(): HitSurface[] {
  const surfaces: HitSurface[] = [];
  for (const kind of CLOSED_ANCHOR_KINDS) {
    const el = anchors.get(kind);
    if (!el) continue;
    if (kind !== "peek") {
      surfaces.push({ box: viewportBox(el) });
      continue;
    }
    const within = viewportBox(el);
    for (const part of el.querySelectorAll("[data-wx-hit]")) {
      surfaces.push({ box: viewportBox(part), within });
    }
  }
  return surfaces;
}
